import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import toast from "react-hot-toast";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import Table, { TableColumn } from "../../../../components/table/Table";
import FilterChipBar from "../../../../components/filter-chip/FilterChipBar";
import DropdownMenu, {
  DropdownMenuItem,
} from "../../../../components/dropdown-menu/DropdownMenu";
import Modal from "../../../../components/modal/Modal";
import ConfirmModal from "../../../../components/modal/ConfirmModal";
import MediaPreviewModal from "../../../../components/media-preview/components/MediaPreviewModal";
import MediaDetailModal from "../../../../components/media-preview/components/MediaDetailModal";
import MediaTranscriptionModal from "../../../../components/media-preview/components/MediaTranscriptionModal";
import BulkTranscriptionModal, {
  BulkTranscriptionTarget,
} from "../../../../components/transcription/BulkTranscriptionModal";
import TranscriptionStatusCell from "../../../../components/transcription/TranscriptionStatusCell";
import {
  collectTranscriptionTargets,
  isTranscribableFileType,
  parseDurationSeconds,
} from "../../../../components/transcription/transcriptionTargets";
import useTranscriptionStatuses, {
  TranscriptionCellInfo,
} from "../../../../hooks/useTranscriptionStatuses";
import BulkDownloadProgressModal from "../../../../components/message/BulkDownloadProgressModal";
import InlineLoading from "../../../../components/loading/InlineLoading";
import UserAvatar from "../../../../components/avatar/UserAvatar";
import { useBulkDownload } from "../../../../hooks/useBulkDownload";
import { useDebouncedValue } from "../../../../hooks/useDebouncedValue";
import {
  dropTargetRowStyle,
  useDropDwellGate,
  useDropDwellReady,
} from "../../../../hooks/useDropDwell";
import { MediaType } from "../../../../types/media-preview";
import { getFileIconFromCategoryOrExtension } from "../../../../components/file-icon/fileIcon";
import { downloadFile } from "../../../../utils/downloadFile";
import { getAcrIconMeta, getDetailLabelWithAcr } from "../../../../utils/acr";
import {
  useAudioAudition,
  AuditionItem,
  AuditionRowProps,
} from "../../../../components/audio-preview/core/useAudioAudition";
import useS3Upload, {
  FileWithPath,
  getFolderPathFromRelativePath,
} from "../../../../hooks/useS3Upload";
import { getFilesFromDataTransferItems } from "../../../../utils/dataTransferEntries";
import { useUploadFailureDialog } from "../../../../components/upload-progress/useUploadFailureDialog";
import {
  AddProjectMessageFileResponse,
  ProjectMessage,
  ProjectMessageFile,
  ProjectMessageFolder,
  useAddProjectMessageFileMutation,
  useCreateProjectMessageFolderMutation,
  useDeleteProjectMessageFileMutation,
  useDeleteProjectMessageFolderMutation,
  useUpdateProjectMessageFileMutation,
} from "../../../../app/services/projectMessages";
import { useGetProjectQuery } from "../../../../app/services/projects";
import StatusBadge from "../../../../components/status-badge/StatusBadge";
import { isProducerGroupPosition } from "../../../../utils/memberPositionUtils";
import ShareLinkModal, { ShareTarget } from "./ShareLinkModal";
import {
  FileRow,
  FileTableFolderNode,
  FileTableSortField,
  FileTableSortState,
  FileTableTag,
  buildFileRows,
  buildFolderInfos,
  buildFileTableTree,
  filterFoldersByTag,
  filterRowsByTag,
  flattenFolderNodeToEntries,
  formatDateTime,
  formatFileSize,
  sortFileRows,
  sortFileTableTree,
} from "./fileTableUtils";

// ===== Props =====

export interface TalkRoomFileTableProps {
  projectId: string;
  /**
   * 上端に確保する追加余白（px）。TalkRoom のフロートするセグメントコントロール
   * の下に潜り込む分。スクロール時はテーブルがこの余白を通過して上まで描画される。
   */
  topInset?: number;
  messages: ProjectMessage[];
  /** 自分のメンバーシップポジション（受領/送付の分類基準） */
  myPosition: string;
  currentUserId?: number;
  /**
   * 全メッセージ（= 全ファイル）の読み込みが完了していない間 true。
   * 完了するまでテーブル最下部にローディング行を表示する。
   */
  isLoadingAll?: boolean;
  /**
   * 検索テキスト（ヘッダーの検索フィールドから。フォルダ名・ファイル名の部分一致）。
   * debounce・低優先レンダー化は内部で行うため、呼び出し側での遅延処理は不要。
   */
  searchText?: string;
  /** ダウンロード既読の記録（TalkRoom の handleFileDownload） */
  // eslint-disable-next-line no-unused-vars
  onFileDownload?: (messageId: string, fileId: string) => void;
  /** リネーム・差し替え成功時にローカルの allMessages を更新するためのコールバック */
  onFileUpdated?: (
    // eslint-disable-next-line no-unused-vars
    messageId: string,
    // eslint-disable-next-line no-unused-vars
    fileId: string,
    // eslint-disable-next-line no-unused-vars
    patch: Partial<ProjectMessageFile>,
  ) => void;
  /** 削除成功時にローカルの allMessages からファイルを取り除くためのコールバック */
  // eslint-disable-next-line no-unused-vars
  onFileDeleted?: (messageId: string, fileId: string) => void;
  /** 外部D&Dアップロード成功時に新規ファイルをローカルの allMessages へ追加するコールバック */
  // eslint-disable-next-line no-unused-vars
  onFileAdded?: (messageId: string, file: ProjectMessageFile) => void;
  /** フォルダ作成成功時のコールバック */
  // eslint-disable-next-line no-unused-vars
  onFolderCreated?: (messageId: string, folder: ProjectMessageFolder) => void;
  /** フォルダ削除成功時のコールバック */
  // eslint-disable-next-line no-unused-vars
  onFolderDeleted?: (messageId: string, folderId: string) => void;
  /** フォルダ情報の更新（共有状態など）をローカルへ反映するコールバック */
  onFolderUpdated?: (
    // eslint-disable-next-line no-unused-vars
    messageId: string,
    // eslint-disable-next-line no-unused-vars
    folderId: string,
    // eslint-disable-next-line no-unused-vars
    patch: Partial<ProjectMessageFolder>,
  ) => void;
}

// ===== styled =====

const Container = styled.div<{ $topInset: number }>`
  flex: 1;
  width: 100%;
  overflow-y: auto;
  padding: ${({ $topInset }) => 8 + $topInset}px 16px 120px;
`;

/**
 * ルート（一番上の階層）へのドロップを受ける器。横スクロール・枠・余白は
 * `Table` 内蔵のスクロール領域に任せ、ここはドロップ中のハイライトだけを担う。
 */
const TableWrapper = styled.div.attrs({
  className: "u-w100",
})<{ $dropActive?: boolean }>`
  border-radius: 4px;
  ${({ $dropActive }) =>
    $dropActive
      ? `
    background: rgba(0, 154, 206, 0.06);
    box-shadow: inset 0 0 0 2px var(--soremo-blue);
  `
      : ""}
`;

// 名前セル内のレイアウト（左: 名前ボタン / 右: ダウンロード既読アバター）
const NameCellInner = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  min-width: 0;
`;

// ダウンロード既読アバター（メッセンジャーの DownloadReceiptsContainer と同見た目）
const ReceiptsContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;
  padding: 0 2px;

  & > * {
    margin-left: -4px;

    &:first-child {
      margin-left: 0;
    }
  }

  & > * > div {
    box-shadow: none;
  }
`;

const NameCellButton = styled.button.attrs({ className: "bodytext-13" })<{
  $depth: number;
  $clickable: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  padding: 2px 0;
  padding-left: ${({ $depth }) => $depth * 20}px;
  border: none;
  background: transparent;
  color: inherit;
  appearance: none;
  -webkit-appearance: none;
  text-align: left;
  overflow: hidden;
  white-space: nowrap;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};

  .file-name-text {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .material-symbols-rounded {
    font-size: 18px;
    flex-shrink: 0;
  }

  .expand-icon {
    font-size: 18px;
    transition: transform 0.15s ease;
  }

  .expand-icon.expanded {
    transform: rotate(90deg);
  }
`;

const MetaCell = styled.td.attrs({ className: "bodytext-13" })`
  color: var(--soremo-gray);
`;

const FolderCount = styled.span.attrs({ className: "bodytext-13" })`
  color: var(--soremo-light-gray);
  flex-shrink: 0;
`;

const ActionsCell = styled.td`
  /* DropdownMenu(Tippy) がセル内でクリップされないようにする */
  overflow: visible !important;
  text-align: center;
`;

const EmptyState = styled.div.attrs({ className: "u-col-center u-w100" })`
  padding: 48px 0;
`;

const RenameInput = styled.input.attrs({ type: "text" })`
  width: 100%;
`;

// D&D 中にカーソルへ追従するゴースト表示
const DragGhost = styled.div.attrs({ className: "bodytext-13" })`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: #fff;
  border: 1px solid var(--soremo-blue);
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  white-space: nowrap;

  .material-symbols-rounded {
    font-size: 18px;
  }
`;

// ===== 表示行（ツリーをフラット化した描画用リスト） =====

type DisplayRow =
  | {
      kind: "folder";
      node: FileTableFolderNode;
      rootId: string;
      /** 検索結果のフラット表示（インデントなし・展開不可） */
      flat?: boolean;
    }
  | { kind: "file"; row: FileRow; depth: number; rootId?: string };

const flattenForDisplay = (
  rootFolders: FileTableFolderNode[],
  rootRows: FileRow[],
  expandedIds: Set<string>,
): DisplayRow[] => {
  const result: DisplayRow[] = [];
  const walk = (node: FileTableFolderNode, rootId: string): void => {
    result.push({ kind: "folder", node, rootId });
    if (!expandedIds.has(node.id)) return;
    node.children.forEach((child) => walk(child, rootId));
    node.rows.forEach((row) => {
      result.push({ kind: "file", row, depth: node.depth + 1, rootId });
    });
  };
  rootFolders.forEach((node) => walk(node, node.id));
  rootRows.forEach((row) => {
    result.push({ kind: "file", row, depth: 0 });
  });
  return result;
};

const PREVIEWABLE_TYPES = ["image", "video", "audio", "document"];

const toMediaType = (fileType: string): MediaType =>
  (fileType === "document" ? "pdf" : fileType) as MediaType;

// ===== D&D 用の行コンポーネント =====

/** ドラッグ中のファイル情報（同一ルートフォルダ内のフォルダのみドロップ可能） */
interface ActiveDrag {
  row: FileRow;
  rootId: string;
}

/**
 * フォルダ内ファイルの行（ドラッグ元）。rootId が無い（フォルダ外の）行はドラッグ不可。
 * オーディオ行は試聴（audition）の行選択・キーボード操作 props を合成する。
 */
const DraggableFileTr: React.FC<{
  row: FileRow;
  rootId?: string;
  auditionRowProps?: AuditionRowProps;
  auditionSelected?: boolean;
  children: React.ReactNode;
}> = ({ row, rootId, auditionRowProps, auditionSelected, children }) => {
  const dragData = useMemo(() => ({ row, rootId }), [row, rootId]);
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: `file:${row.file.id}`,
    data: dragData,
    disabled: !rootId,
  });
  const {
    ref: auditionRef,
    style: auditionStyle,
    ...auditionRest
  } = auditionRowProps ?? {};
  return (
    <tr
      ref={(el) => {
        setNodeRef(el);
        auditionRef?.(el);
      }}
      {...listeners}
      {...attributes}
      {...auditionRest}
      style={{
        opacity: isDragging ? 0.4 : 1,
        // タッチは長押しでドラッグを開始するため、待機中のスクロールは
        // ブラウザに任せる（"none" にするとこの行から一覧をスクロールできない）。
        touchAction: rootId ? "manipulation" : undefined,
        // 長押し中に iOS の選択メニューが出るのを抑える
        WebkitTouchCallout: rootId ? "none" : undefined,
        background: auditionSelected ? "rgba(0, 154, 206, 0.06)" : undefined,
        ...auditionStyle,
      }}
    >
      {children}
    </tr>
  );
};

/**
 * フォルダ行（ドロップ先）。
 * - テーブル内D&D: ドラッグ中ファイルと同じルート配下のみ受け付ける
 * - 外部D&D: OSからのファイルドロップでこのフォルダへアップロードする
 *   （`onNativeFileDrop` が渡されたフォルダのみ = 自分の陣営のフォルダ）
 */
const DroppableFolderTr: React.FC<{
  node: FileTableFolderNode;
  rootId: string;
  activeDrag: ActiveDrag | null;
  // eslint-disable-next-line no-unused-vars
  onNativeFileDrop?: (files: File[]) => void;
  children: React.ReactNode;
}> = ({ node, rootId, activeDrag, onNativeFileDrop, children }) => {
  const isValidTarget =
    !!activeDrag &&
    activeDrag.rootId === rootId &&
    activeDrag.row.file.folder?.id !== node.id;
  const { setNodeRef, isOver } = useDroppable({
    id: `folder:${node.id}`,
    data: { node, rootId },
    disabled: !isValidTarget,
  });

  // 外部（OS）ファイルドラッグのホバー状態
  const [isNativeOver, setIsNativeOver] = useState(false);

  const isExternalFileDrag = (e: React.DragEvent): boolean =>
    !!onNativeFileDrop && Array.from(e.dataTransfer.types).includes("Files");

  const handleNativeDragOver = (e: React.DragEvent) => {
    if (!isExternalFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsNativeOver(true);
  };

  const handleNativeDrop = (e: React.DragEvent) => {
    if (!isExternalFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setIsNativeOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) onNativeFileDrop!(files);
  };

  const dndOver = isValidTarget && isOver;
  // 滞留時間を要求するのはテーブル内 D&D（移動）だけ。OS からのドロップは
  // アップロードなので、誤って移動が確定してしまう問題は起きない。
  const dwellReady = useDropDwellReady(dndOver);

  return (
    <tr
      ref={setNodeRef}
      onDragOver={handleNativeDragOver}
      onDragLeave={() => setIsNativeOver(false)}
      onDrop={handleNativeDrop}
      style={
        isValidTarget || isNativeOver
          ? dropTargetRowStyle({
              isOver: dndOver || isNativeOver,
              isReady: dwellReady || isNativeOver,
            })
          : undefined
      }
    >
      {children}
    </tr>
  );
};

// ===== 行コンポーネント（メモ化） =====
//
// 行数が多くなり得るため、行は React.memo で囲み、親（TalkRoomFileTable）の
// 再レンダー（検索テキストのタイプ・試聴の再生位置 RAF 更新など）で全行が
// 再レンダーされないようにする。親から渡す props は安定参照のみにすること。

/** フォルダ行（行全体 = DroppableFolderTr + セル群） */
const TalkRoomFolderRow = React.memo<{
  node: FileTableFolderNode;
  rootId: string;
  /** 検索結果のフラット表示（インデントなし・展開不可） */
  flat: boolean;
  isExpanded: boolean;
  activeDrag: ActiveDrag | null;
  /** 外部（OS）D&D アップロードを受け付けるか */
  canNativeDrop: boolean;
  onToggle: (_folderId: string) => void;
  onExternalDrop: (_node: FileTableFolderNode, _files: File[]) => void;
  buildMenuItems: (_node: FileTableFolderNode) => DropdownMenuItem[];
}>(
  ({
    node,
    rootId,
    flat,
    isExpanded,
    activeDrag,
    canNativeDrop,
    onToggle,
    onExternalDrop,
    buildMenuItems,
  }) => (
    <DroppableFolderTr
      node={node}
      rootId={rootId}
      activeDrag={activeDrag}
      onNativeFileDrop={
        canNativeDrop ? (files) => onExternalDrop(node, files) : undefined
      }
    >
      <td>
        <NameCellButton
          type="button"
          $depth={flat ? 0 : node.depth}
          $clickable={!flat}
          onClick={flat ? undefined : () => onToggle(node.id)}
          // フラット表示では操作を持たないため、フォーカス対象から外す
          tabIndex={flat ? -1 : undefined}
        >
          {flat ? (
            // 検索中は階層外に切り出したフラット表示のため、展開はできない
            <span className="material-symbols-rounded">folder</span>
          ) : (
            <span
              className={`material-symbols-rounded expand-icon${isExpanded ? " expanded" : ""}`}
            >
              chevron_right
            </span>
          )}
          <span className="file-name-text">{node.name}</span>
          <FolderCount>{node.totalCount}件</FolderCount>
          {/* 進行中モーダルの「旧環境」chip と同じデザイン */}
          {node.isShared && (
            <StatusBadge label="共有中" size="small" color="border" />
          )}
        </NameCellButton>
      </td>
      <MetaCell>{formatDateTime(node.latestUpdated)}</MetaCell>
      <MetaCell>{formatFileSize(node.totalSize)}</MetaCell>
      <ActionsCell>
        <DropdownMenu items={buildMenuItems(node)} />
      </ActionsCell>
    </DroppableFolderTr>
  ),
);
TalkRoomFolderRow.displayName = "TalkRoomFolderRow";

/** 試聴行のフォーカスリング打ち消し（useAudioAudition.getRowProps と同一） */
const AUDITION_ROW_STYLE: React.CSSProperties = { outline: "none" };

/** ファイル行（行全体 = DraggableFileTr + セル群） */
const TalkRoomFileRow = React.memo<{
  row: FileRow;
  depth: number;
  rootId?: string;
  auditionSelected: boolean;
  auditionPlaying: boolean;
  onPreview: (_row: FileRow) => void;
  onShowDetail: (_row: FileRow) => void;
  onAuditionToggle: (_key: string) => void;
  onAuditionRowRef: (_key: string, _el: HTMLElement | null) => void;
  onAuditionRowClick: (_key: string) => void;
  onAuditionRowKeyDown: (
    _key: string,
    _e: React.KeyboardEvent<HTMLElement>,
  ) => void;
  buildMenuItems: (_row: FileRow) => DropdownMenuItem[];
  /** 文字起こしステータス / 本文プレビュー（未実行なら undefined で非表示） */
  transcriptionStatus?: TranscriptionCellInfo;
}>(
  ({
    row,
    depth,
    rootId,
    auditionSelected,
    auditionPlaying,
    onPreview,
    onShowDetail,
    onAuditionToggle,
    onAuditionRowRef,
    onAuditionRowClick,
    onAuditionRowKeyDown,
    buildMenuItems,
    transcriptionStatus,
  }) => {
    const isAudio = row.file.file_type === "audio";
    const hasSrc = !!(row.file.optimized_url || row.file.original_url);
    // オーディオは行クリック = 試聴選択（プレビューはメニューから）
    const isAuditionable = isAudio && hasSrc;
    const isPreviewable =
      !isAudio && PREVIEWABLE_TYPES.includes(row.file.file_type) && hasSrc;
    const acrIcon = getAcrIconMeta(row.file.acr_scan_status);
    const downloadedUsers = row.file.downloaded_users ?? [];
    // useAudioAudition.getRowProps 相当の行 props を安定コールバックから組み立てる
    // （getRowProps 本体は再レンダーごとに参照が変わり、memo を無効化するため）
    const auditionRowProps: AuditionRowProps | undefined = isAuditionable
      ? {
          ref: (el) => onAuditionRowRef(row.file.id, el),
          tabIndex: auditionSelected ? 0 : -1,
          onClick: () => onAuditionRowClick(row.file.id),
          onKeyDown: (e) => onAuditionRowKeyDown(row.file.id, e),
          style: AUDITION_ROW_STYLE,
        }
      : undefined;
    return (
      <DraggableFileTr
        row={row}
        rootId={rootId}
        auditionRowProps={auditionRowProps}
        auditionSelected={isAuditionable && auditionSelected}
      >
        <td>
          <NameCellInner>
            <NameCellButton
              type="button"
              $depth={depth}
              $clickable={isPreviewable}
              onClick={() => isPreviewable && onPreview(row)}
            >
              {isAuditionable ? (
                <span
                  className="material-symbols-rounded"
                  style={{
                    fontSize: 20,
                    color: auditionPlaying
                      ? "var(--soremo-blue)"
                      : "var(--soremo-light-gray)",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAuditionToggle(row.file.id);
                  }}
                >
                  {auditionPlaying ? "pause_circle" : "play_circle"}
                </span>
              ) : (
                <span className="material-symbols-rounded">
                  {getFileIconFromCategoryOrExtension(
                    row.file.file_type,
                    row.file.file_name,
                  )}
                </span>
              )}
              <span className="file-name-text">{row.file.file_name}</span>
              {/* 進行中モーダルの「旧環境」chip と同じデザイン */}
              {row.file.is_shared && (
                <StatusBadge label="共有中" size="small" color="border" />
              )}
              <TranscriptionStatusCell entry={transcriptionStatus} />
              {isAudio && acrIcon.show && (
                <span
                  className="material-symbols-rounded"
                  style={{
                    fontSize: 18,
                    color: acrIcon.color,
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowDetail(row);
                  }}
                >
                  error
                </span>
              )}
            </NameCellButton>
            {/* ダウンロード既読アバター（メッセンジャーと同じ表現） */}
            {downloadedUsers.length > 0 && (
              <ReceiptsContainer>
                {downloadedUsers.map((user) => (
                  <UserAvatar
                    key={user.id}
                    userId={user.id}
                    avatar={user.avatar}
                    displayName={user.display_name}
                    size={12}
                  />
                ))}
              </ReceiptsContainer>
            )}
          </NameCellInner>
        </td>
        <MetaCell>{formatDateTime(row.updatedAt)}</MetaCell>
        <MetaCell>{formatFileSize(row.sizeBytes)}</MetaCell>
        <ActionsCell>
          <DropdownMenu items={buildMenuItems(row)} />
        </ActionsCell>
      </DraggableFileTr>
    );
  },
);
TalkRoomFileRow.displayName = "TalkRoomFileRow";

/** files POST（外部D&Dアップロード）のレスポンスを onFileAdded へ渡す形へ変換する */
const toProjectMessageFile = (
  data: AddProjectMessageFileResponse["data"],
): ProjectMessageFile => ({
  id: data.id,
  file_key: data.file_key ?? undefined,
  original_url: data.original_url ?? "",
  file_name: data.file_name,
  file_type: data.file_type,
  file_metadata: data.file_metadata,
  folder: data.folder ?? undefined,
  updated_at: data.updated_at,
});

/**
 * トークルームのファイル一覧（ストレージビュー）。
 *
 * - 受領 / 送付タグ（自分のポジションの相手側ファイルを「受領」として初期表示）
 * - フォルダ階層を展開/折りたたみ可能なツリー行で表現（ファイル名クリックでプレビュー）
 * - 列ヘッダークリックでソート（ファイル名 / 更新日 / サイズ）
 * - ヘッダー検索でフォルダ名・ファイル名を絞り込み。検索中は階層を出さず、
 *   ヒットした行だけのフラットリストを表示する（大量ファイル対策）
 * - ファイル行の3点リーダー: ダウンロード / 共有（リンク発行モーダル） / リネーム /
 *   差し替え・削除（自分の陣営のファイルのみ）
 * - フォルダ行の3点リーダー: 一括ダウンロード / 共有 / フォルダ作成・削除（自分の陣営のみ）
 * - D&D: テーブル内でフォルダ間のファイル移動、OSからのドロップでフォルダへアップロード
 * - 受領ファイルタグ選択中のプロデューサー陣営は、テーブル全体への OS ドロップで
 *   「受領ファイル」へ直接アップロードできる（トークルーム非表示の裏方メッセージへ
 *   添付。クライアントから他手段で受け取った資料の集約用。メール通知なし）。
 *   フォルダごとのドロップにも対応し、サブフォルダ構造を保持して登録する
 * - 共有中のファイル / フォルダには「共有中」バッジを表示
 */
const TalkRoomFileTable: React.FC<TalkRoomFileTableProps> = ({
  projectId,
  topInset = 0,
  messages,
  myPosition,
  currentUserId,
  isLoadingAll = false,
  searchText = "",
  onFileDownload,
  onFileUpdated,
  onFileDeleted,
  onFileAdded,
  onFolderCreated,
  onFolderDeleted,
  onFolderUpdated,
}) => {
  const [selectedTag, setSelectedTag] = useState<FileTableTag | null>(
    "received",
  );
  const [sortState, setSortState] = useState<FileTableSortState>({
    field: "updated",
    order: "desc",
  });
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(),
  );
  const [previewRow, setPreviewRow] = useState<FileRow | null>(null);
  const [renameTarget, setRenameTarget] = useState<FileRow | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FileRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // D&D 中のファイル（フォルダ内ファイルのみ）
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null);
  // フォルダ作成モーダル（作成先の親フォルダ）
  const [createFolderParent, setCreateFolderParent] =
    useState<FileTableFolderNode | null>(null);
  const [createFolderName, setCreateFolderName] = useState("");
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  // フォルダ削除確認
  const [deleteFolderTarget, setDeleteFolderTarget] =
    useState<FileTableFolderNode | null>(null);
  const [isDeletingFolder, setIsDeletingFolder] = useState(false);
  // 外部D&Dアップロード中フラグ（フォルダ / 受領ファイル置き場の両方で共用）
  const [isUploadingExternal, setIsUploadingExternal] = useState(false);
  // 共有リンクモーダルの対象
  const [shareTarget, setShareTarget] = useState<ShareTarget | null>(null);
  // オーディオの詳細情報（ACR）モーダルの対象
  const [detailRow, setDetailRow] = useState<FileRow | null>(null);
  // 文字起こしモーダルの対象（動画・オーディオ）
  const [transcriptionRow, setTranscriptionRow] = useState<FileRow | null>(
    null,
  );
  // 一括文字起こしモーダル（フォルダ単位）
  const [bulkTranscription, setBulkTranscription] = useState<{
    targets: BulkTranscriptionTarget[];
    scopeLabel: string;
  } | null>(null);

  const replaceTargetRef = useRef<FileRow | null>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);

  const [updateFile] = useUpdateProjectMessageFileMutation();
  const [deleteFile] = useDeleteProjectMessageFileMutation();
  const [addFile] = useAddProjectMessageFileMutation();
  const [createFolder] = useCreateProjectMessageFolderMutation();
  const [deleteFolder] = useDeleteProjectMessageFolderMutation();

  // テーブル内D&D（ファイル移動）用センサー。行全体がドラッグ元で、同じ行に
  // 試聴選択のクリックが乗っているため、入力手段ごとにしきい値を分ける
  // （FileBrowser と同じ設定にそろえる）。
  // - マウス: 12px 動かしてからドラッグ開始
  // - タッチ: 250ms 長押ししてから開始。待機中に 5px 動いたらスクロール意図
  //   として起動を取り消す
  const dndSensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 12 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
  );

  // 差し替え・フォルダへの外部D&Dアップロード共用の S3 アップロード。
  // DB 登録は各 API（ファイル更新 / ファイル追加）側で行うため register はスキップする
  const {
    uploadFiles: uploadFilesToS3,
    clearCompleted,
    retryFiles,
    beginPostProcess,
    endPostProcess,
    withPostProcess,
  } = useS3Upload({
    purpose: "project_message",
    projectId,
    skipRegister: true,
  });

  // アップロード部分失敗時の「再試行 / 失敗分を除いて続行 / キャンセル」ダイアログ
  const { resolveUploadFailures, uploadFailureDialog } = useUploadFailureDialog({
    retryFiles,
  });

  // ===== 行データ =====

  const allRows = useMemo(
    () => buildFileRows(messages, myPosition, currentUserId),
    [messages, myPosition, currentUserId],
  );

  // zip 一括ダウンロードの既読記録用: fileId → messageId の逆引き
  const fileIdToMessageId = useMemo(() => {
    const map = new Map<string, string>();
    allRows.forEach((r) => map.set(r.file.id, r.messageId));
    return map;
  }, [allRows]);

  // 文字起こしステータス（チップ / 本文プレビュー表示）。音声・動画だけを
  // 購読し、実行中が残っている間はフックがポーリングして一覧を自動更新する。
  const transcribableFileIds = useMemo(
    () =>
      allRows
        .filter((r) => isTranscribableFileType(r.file.file_type))
        .map((r) => r.file.id),
    [allRows],
  );
  const transcriptionStatuses = useTranscriptionStatuses(transcribableFileIds);

  // フォルダの zip 一括ダウンロード（メッセンジャーと同じ仕組み）
  const [downloadingFolderName, setDownloadingFolderName] = useState("");
  const handleZipFileDownloaded = useCallback(
    (fileId: string) => {
      const messageId = fileIdToMessageId.get(fileId);
      if (messageId) onFileDownload?.(messageId, fileId);
    },
    [fileIdToMessageId, onFileDownload],
  );
  const {
    downloadState,
    isDownloading: isZipDownloading,
    startDownload,
    closeDownloadModal,
  } = useBulkDownload(handleZipFileDownloaded);

  const handleFolderDownload = useCallback(
    (node: FileTableFolderNode) => {
      setDownloadingFolderName(node.name);
      startDownload(flattenFolderNodeToEntries(node), node.name);
    },
    [startDownload],
  );

  // filterRowsByTag と同じ判定（非表示メッセージの添付は常に「受領」扱い）
  const hasReceived = useMemo(
    () =>
      allRows.some((r) => r.isHidden || (!r.isFromMyGroup && !r.isOwnMessage)),
    [allRows],
  );
  const hasSent = useMemo(
    () =>
      allRows.some((r) => !r.isHidden && (r.isFromMyGroup || r.isOwnMessage)),
    [allRows],
  );

  const tagItems = useMemo(() => {
    const items: { id: string; name: string }[] = [];
    if (hasReceived) items.push({ id: "received", name: "受領ファイル" });
    if (hasSent) items.push({ id: "sent", name: "送付ファイル" });
    return items;
  }, [hasReceived, hasSent]);

  // 受領ファイルが1件も無い場合は「すべて」にフォールバック
  const effectiveTag: FileTableTag | null =
    selectedTag === "received" && !hasReceived
      ? null
      : selectedTag === "sent" && !hasSent
        ? null
        : selectedTag;

  // メッセージ紐付けフォルダ（空フォルダ含む）
  const allFolderInfos = useMemo(
    () => buildFolderInfos(messages, myPosition, currentUserId),
    [messages, myPosition, currentUserId],
  );

  // 検索テキストは2段階で遅延反映する（大量ファイルでのフリーズ対策）。
  // 1. debounce: 1文字ごとの中間値で重い再計算が走らないようにする
  // 2. useDeferredValue: 反映時の再計算・再描画を低優先レンダーに逃がし、
  //    タイピング（urgent update）をブロックしない
  const debouncedSearchText = useDebouncedValue(searchText, 200);
  const deferredSearchText = useDeferredValue(debouncedSearchText);
  const searchQuery = deferredSearchText.trim().toLowerCase();

  // タグ（受領/送付）で絞った行
  const taggedRows = useMemo(
    () => filterRowsByTag(allRows, effectiveTag),
    [allRows, effectiveTag],
  );

  // タグ絞り込み後のソート済みツリー。検索テキストを依存に含めず、検索のたびに
  // O(全件) のツリー再構築・集計・ソートが走らないようにする。
  const sortedTree = useMemo(() => {
    const { rootFolders, rootRows } = buildFileTableTree(
      taggedRows,
      filterFoldersByTag(allFolderInfos, effectiveTag),
    );
    sortFileTableTree(rootFolders, sortState);
    return { rootFolders, rootRows: sortFileRows(rootRows, sortState) };
  }, [taggedRows, allFolderInfos, effectiveTag, sortState]);

  const displayRows = useMemo(() => {
    // 通常表示: ツリーを展開状態に従ってフラット化
    if (!searchQuery) {
      return flattenForDisplay(
        sortedTree.rootFolders,
        sortedTree.rootRows,
        expandedFolders,
      );
    }
    // 検索中: 階層・祖先の復元はせず、名前がヒットしたフォルダとファイル
    // だけのフラットリストを表示する（以前の「全フォルダ展開」は大量
    // ファイルで数千行の DOM を毎回描画し、フリーズの要因になり得た）。
    const rows: DisplayRow[] = [];
    const walkFolders = (
      nodes: FileTableFolderNode[],
      rootId: string,
    ): void => {
      nodes.forEach((node) => {
        if (node.name.toLowerCase().includes(searchQuery)) {
          rows.push({ kind: "folder", node, rootId, flat: true });
        }
        walkFolders(node.children, rootId);
      });
    };
    sortedTree.rootFolders.forEach((node) => walkFolders([node], node.id));
    const matchedRows = sortFileRows(
      taggedRows.filter((r) =>
        r.file.file_name.toLowerCase().includes(searchQuery),
      ),
      sortState,
    );
    matchedRows.forEach((row) => {
      rows.push({ kind: "file", row, depth: 0 });
    });
    return rows;
  }, [sortedTree, taggedRows, sortState, expandedFolders, searchQuery]);

  // 表示中のオーディオ行を表示順に集めて試聴対象にする（行選択 + ↑↓ + Space）
  const audioAuditionItems = useMemo<AuditionItem[]>(() => {
    const items: AuditionItem[] = [];
    displayRows.forEach((entry) => {
      if (entry.kind !== "file" || entry.row.file.file_type !== "audio") return;
      const src = entry.row.file.optimized_url || entry.row.file.original_url;
      if (src) items.push({ key: entry.row.file.id, src });
    });
    return items;
  }, [displayRows]);

  const audition = useAudioAudition(audioAuditionItems);

  // audition の API（getRowProps 等）は再生位置の RAF 更新のたびに参照が
  // 変わるため、メモ化した TalkRoomFileRow へは ref 経由の安定したラッパーを
  // 渡す（再生中に全行が毎フレーム再レンダーされるのを防ぐ）。
  const auditionRef = useRef(audition);
  useEffect(() => {
    auditionRef.current = audition;
  });
  const handleAuditionRowRef = useCallback(
    (key: string, el: HTMLElement | null) => {
      auditionRef.current.getRowProps(key).ref(el);
    },
    [],
  );
  const handleAuditionRowClick = useCallback((key: string) => {
    auditionRef.current.select(key);
  }, []);
  const handleAuditionRowKeyDown = useCallback(
    (key: string, e: React.KeyboardEvent<HTMLElement>) => {
      auditionRef.current.getRowProps(key).onKeyDown(e);
    },
    [],
  );
  const handleAuditionToggle = useCallback((key: string) => {
    auditionRef.current.toggle(key);
  }, []);

  // ===== ハンドラー =====

  const handleSort = useCallback((field: FileTableSortField) => {
    setSortState((prev) => ({
      field,
      order: prev.field === field && prev.order === "asc" ? "desc" : "asc",
    }));
  }, []);

  const toggleFolder = useCallback((folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  const handleDownload = useCallback(
    (row: FileRow) => {
      downloadFile(row.file.original_url, row.file.file_name);
      onFileDownload?.(row.messageId, row.file.id);
    },
    [onFileDownload],
  );

  // 共有リンクの有効期限の初期値: プロジェクトの開発完了日（YYYY-MM-DD）
  const { data: projectData } = useGetProjectQuery(
    { project_id: projectId },
    { skip: !projectId },
  );
  const projectEndDate =
    projectData?.data?.project?.end_date?.slice(0, 10) || null;

  const handleShare = useCallback((row: FileRow) => {
    setShareTarget({
      type: "file",
      id: row.file.id,
      name: row.file.file_name,
      messageId: row.messageId,
    });
  }, []);

  // 共有ON/OFFの変更をローカルの allMessages（バッジ表示）へ反映する
  const handleShareStateChanged = useCallback(
    (target: ShareTarget, isShared: boolean) => {
      if (!target.messageId) return;
      if (target.type === "file") {
        onFileUpdated?.(target.messageId, target.id, { is_shared: isShared });
      } else {
        onFolderUpdated?.(target.messageId, target.id, { is_shared: isShared });
      }
    },
    [onFileUpdated, onFolderUpdated],
  );

  const openRename = useCallback((row: FileRow) => {
    setRenameTarget(row);
    setRenameDraft(row.file.file_name);
  }, []);

  const handleRenameConfirm = useCallback(async () => {
    if (!renameTarget || isRenaming) return;
    const newName = renameDraft.trim();
    if (!newName || newName === renameTarget.file.file_name) {
      setRenameTarget(null);
      return;
    }
    setIsRenaming(true);
    try {
      const result = await updateFile({
        project_id: projectId,
        file_id: renameTarget.file.id,
        file_name: newName,
      }).unwrap();
      onFileUpdated?.(renameTarget.messageId, renameTarget.file.id, {
        file_name: result.data.file_name,
        updated_at: result.data.updated_at,
      });
      toast.success("ファイル名を変更しました");
      setRenameTarget(null);
    } catch {
      toast.error("ファイル名の変更に失敗しました");
    } finally {
      setIsRenaming(false);
    }
  }, [
    renameTarget,
    renameDraft,
    isRenaming,
    projectId,
    updateFile,
    onFileUpdated,
  ]);

  const openReplace = useCallback((row: FileRow) => {
    replaceTargetRef.current = row;
    replaceInputRef.current?.click();
  }, []);

  const handleReplaceFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const target = replaceTargetRef.current;
      const file = e.target.files?.[0];
      // 同じファイルを再選択できるよう毎回リセットする
      e.target.value = "";
      if (!target || !file || isReplacing) return;

      setIsReplacing(true);
      const toastId = toast.loading(
        `「${target.file.file_name}」を差し替え中...`,
      );
      try {
        const uploadResults = await uploadFilesToS3([file]);
        // 失敗時は「再試行 / キャンセル」ダイアログで解決（1件のため続行選択肢なし）
        const hadFailures = uploadResults.some((r) => r.status === "error");
        const resolved = await resolveUploadFailures(uploadResults);
        if (hadFailures) clearCompleted();
        if (resolved === null) {
          // キャンセル: 差し替えを中断
          toast.dismiss(toastId);
          return;
        }
        const uploaded = resolved.find((r) => r.fileKey);
        if (!uploaded?.fileKey) {
          throw new Error("upload failed");
        }
        const result = await withPostProcess(
          () =>
            updateFile({
              project_id: projectId,
              file_id: target.file.id,
              file_key: uploaded.fileKey!,
            }).unwrap(),
          "ファイルを差し替え中",
        );
        onFileUpdated?.(target.messageId, target.file.id, {
          file_key: result.data.file_key ?? undefined,
          original_url: result.data.original_url ?? undefined,
          file_name: result.data.file_name,
          file_type: result.data.file_type,
          file_metadata: result.data.file_metadata,
          updated_at: result.data.updated_at,
        });
        toast.success("ファイルを差し替えました", { id: toastId });
      } catch {
        toast.error("ファイルの差し替えに失敗しました", { id: toastId });
      } finally {
        replaceTargetRef.current = null;
        setIsReplacing(false);
      }
    },
    [
      isReplacing,
      uploadFilesToS3,
      resolveUploadFailures,
      clearCompleted,
      updateFile,
      withPostProcess,
      projectId,
      onFileUpdated,
    ],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteFile({
        project_id: projectId,
        file_id: deleteTarget.file.id,
      }).unwrap();
      onFileDeleted?.(deleteTarget.messageId, deleteTarget.file.id);
      toast.success("ファイルを削除しました");
      setDeleteTarget(null);
    } catch {
      toast.error("ファイルの削除に失敗しました");
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, isDeleting, projectId, deleteFile, onFileDeleted]);

  // ===== フォルダ作成・削除 =====

  const handleCreateFolderConfirm = useCallback(async () => {
    if (!createFolderParent || isCreatingFolder) return;
    const name = createFolderName.trim();
    if (!name) return;
    setIsCreatingFolder(true);
    try {
      const result = await createFolder({
        project_id: projectId,
        parent_folder_id: createFolderParent.id,
        name,
      }).unwrap();
      const messageId = result.data.message_id ?? createFolderParent.messageId;
      if (messageId) {
        onFolderCreated?.(messageId, {
          id: result.data.id,
          name: result.data.name,
          parent_id: result.data.parent_id,
        });
      }
      // 作成先を展開して新フォルダを見せる
      setExpandedFolders((prev) => new Set([...prev, createFolderParent.id]));
      toast.success(`フォルダ「${result.data.name}」を作成しました`);
      setCreateFolderParent(null);
      setCreateFolderName("");
    } catch {
      toast.error("フォルダの作成に失敗しました");
    } finally {
      setIsCreatingFolder(false);
    }
  }, [
    createFolderParent,
    createFolderName,
    isCreatingFolder,
    projectId,
    createFolder,
    onFolderCreated,
  ]);

  const handleDeleteFolderConfirm = useCallback(async () => {
    if (!deleteFolderTarget || isDeletingFolder) return;
    setIsDeletingFolder(true);
    try {
      const result = await deleteFolder({
        project_id: projectId,
        folder_id: deleteFolderTarget.id,
      }).unwrap();
      const messageId = result.data.message_id ?? deleteFolderTarget.messageId;
      if (messageId) {
        onFolderDeleted?.(messageId, deleteFolderTarget.id);
      }
      toast.success("フォルダを削除しました");
      setDeleteFolderTarget(null);
    } catch {
      toast.error(
        "フォルダの削除に失敗しました（空のフォルダのみ削除できます）",
      );
    } finally {
      setIsDeletingFolder(false);
    }
  }, [
    deleteFolderTarget,
    isDeletingFolder,
    projectId,
    deleteFolder,
    onFolderDeleted,
  ]);

  // ===== 外部ファイルのD&Dアップロード =====
  //
  // 宛先は 2 系統: フォルダ行へのドロップと、「受領ファイル」タグ選択中の
  // テーブル全体へのドロップ（受領ファイルへの直接アップロード）。
  // S3 アップロード〜files POST 登録の流れは uploadDroppedFiles で共通。

  /**
   * ドロップされたファイル群を S3 へアップロードし、register で 1 件ずつ
   * 登録する。アップロード失敗があれば「再試行 / 失敗分を除いて続行 /
   * キャンセル」ダイアログで解決する。
   * フォルダ D&D（FileWithPath[]）の場合は register に相対パスも渡す。
   * 戻り値は登録に成功した件数（ユーザーがキャンセルした場合は null）。
   */
  const uploadDroppedFiles = useCallback(
    async (
      files: File[] | FileWithPath[],
      // eslint-disable-next-line no-unused-vars
      register: (fileKey: string, relativePath?: string) => Promise<void>,
    ): Promise<number | null> => {
      const uploadResults = await uploadFilesToS3(files);
      const hadFailures = uploadResults.some((r) => r.status === "error");
      const resolved = await resolveUploadFailures(uploadResults);
      if (hadFailures) clearCompleted();
      if (resolved === null) return null;
      const succeeded = resolved.filter((r) => r.fileKey);
      let added = 0;
      // S3 転送が終わっても登録が残っているあいだは「処理中」として扱う
      // （タブを閉じようとしたら引き止める）
      beginPostProcess("ファイルを登録中");
      try {
        for (const uploaded of succeeded) {
          try {
            await register(uploaded.fileKey!, uploaded.relativePath);
            added += 1;
          } catch {
            // 個別失敗はカウントに反映して続行
          }
        }
      } finally {
        endPostProcess();
      }
      return added;
    },
    [
      uploadFilesToS3,
      resolveUploadFailures,
      clearCompleted,
      beginPostProcess,
      endPostProcess,
    ],
  );

  // フォルダ行へのドロップ: フォルダが属するメッセージの添付として追加する
  const handleExternalDrop = useCallback(
    async (node: FileTableFolderNode, files: File[]) => {
      if (files.length === 0 || isUploadingExternal) return;
      setIsUploadingExternal(true);
      const toastId = toast.loading(
        `「${node.name}」へ ${files.length}件をアップロード中...`,
      );
      try {
        const added = await uploadDroppedFiles(files, async (fileKey) => {
          const result = await addFile({
            project_id: projectId,
            file_key: fileKey,
            folder_id: node.id,
          }).unwrap();
          const messageId = result.data.message_id ?? node.messageId;
          if (messageId) {
            onFileAdded?.(messageId, toProjectMessageFile(result.data));
          }
        });
        if (added === null) {
          // キャンセル: フォルダへの追加を中断
          toast.dismiss(toastId);
          return;
        }
        setExpandedFolders((prev) => new Set([...prev, node.id]));
        if (added === files.length) {
          toast.success(`${added}件をアップロードしました`, { id: toastId });
        } else {
          toast.error(`${files.length}件中 ${added}件をアップロードしました`, {
            id: toastId,
          });
        }
      } catch {
        toast.error("アップロードに失敗しました", { id: toastId });
      } finally {
        setIsUploadingExternal(false);
      }
    },
    [isUploadingExternal, uploadDroppedFiles, addFile, projectId, onFileAdded],
  );

  // ===== 受領ファイルへの直接アップロード（テーブル全体への外部D&D） =====
  //
  // 受領ファイルタグ選択中のプロデューサー陣営のみ有効。トークルームに
  // 表示されない裏方メッセージ（is_hidden）へ添付するため、クライアントへの
  // メール通知や未読バッジは発生しない（他手段で受領した資料の集約用）。

  const canDropToReceived =
    effectiveTag === "received" && isProducerGroupPosition(myPosition);

  // テーブル全体への外部（OS）ファイルドラッグのホバー状態
  const [isRootDropOver, setIsRootDropOver] = useState(false);

  const handleReceivedRootDrop = useCallback(
    async (files: File[] | FileWithPath[]) => {
      if (files.length === 0 || isUploadingExternal) return;
      setIsUploadingExternal(true);
      const toastId = toast.loading(
        `受領ファイルへ ${files.length}件をアップロード中...`,
      );
      try {
        const added = await uploadDroppedFiles(
          files,
          async (fileKey, relativePath) => {
            // フォルダ D&D の場合は相対パスからフォルダパスを組み立て、
            // 裏方メッセージ配下にサブフォルダ構造ごと登録する
            const folderPath = relativePath
              ? getFolderPathFromRelativePath(relativePath)
              : undefined;
            const result = await addFile({
              project_id: projectId,
              file_key: fileKey,
              hidden: true,
              ...(folderPath && { folder_path: folderPath }),
            }).unwrap();
            const messageId = result.data.message_id;
            if (messageId) {
              // フォルダ（祖先チェーン）を先にローカルへ反映してから
              // ファイルを追加する。初回ドロップ（裏方メッセージ未取得）時は
              // onFileAdded 側のメッセージ単体取得が folders ごと反映する
              result.data.folders?.forEach((folder) => {
                onFolderCreated?.(messageId, folder);
              });
              onFileAdded?.(messageId, toProjectMessageFile(result.data));
            }
          },
        );
        if (added === null) {
          // キャンセル: 受領ファイルへの追加を中断
          toast.dismiss(toastId);
          return;
        }
        if (added === files.length) {
          toast.success(`${added}件を受領ファイルへ追加しました`, {
            id: toastId,
          });
        } else {
          toast.error(`${files.length}件中 ${added}件をアップロードしました`, {
            id: toastId,
          });
        }
      } catch {
        toast.error("アップロードに失敗しました", { id: toastId });
      } finally {
        setIsUploadingExternal(false);
      }
    },
    [
      isUploadingExternal,
      uploadDroppedFiles,
      addFile,
      projectId,
      onFileAdded,
      onFolderCreated,
    ],
  );

  const isExternalFileDragEvent = (e: React.DragEvent): boolean =>
    Array.from(e.dataTransfer.types).includes("Files");

  const handleRootDragOver = (e: React.DragEvent) => {
    if (!canDropToReceived || !isExternalFileDragEvent(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setIsRootDropOver(true);
  };

  const handleRootDragLeave = (e: React.DragEvent) => {
    // 子要素間の移動でも発火するため、領域の外へ出たときだけ解除する
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsRootDropOver(false);
  };

  const handleRootDrop = (e: React.DragEvent) => {
    if (!canDropToReceived || !isExternalFileDragEvent(e)) return;
    e.preventDefault();
    // フォルダ D&D のサブフォルダ構造を保持するため webkitGetAsEntry で走査する。
    // getFilesFromDataTransferItems は DataTransfer が生きているうちに
    // 同期でエントリを収集するため、await を挟まずここで直接呼び出す。
    // フォールバック用の平坦な FileList も同期で確保しておく。
    const plainFiles = Array.from(e.dataTransfer.files);
    if (e.dataTransfer.items?.length) {
      getFilesFromDataTransferItems(e.dataTransfer.items)
        .then((filesWithPath) => {
          // エントリ経由で 1 件も取れなかった場合もドロップを捨てない
          const targets =
            filesWithPath.length > 0 ? filesWithPath : plainFiles;
          if (targets.length > 0) handleReceivedRootDrop(targets);
        })
        .catch(() => {
          // エントリ走査に失敗した場合は従来どおり平坦にアップロードする
          if (plainFiles.length > 0) handleReceivedRootDrop(plainFiles);
        });
      return;
    }
    if (plainFiles.length > 0) handleReceivedRootDrop(plainFiles);
  };

  // ===== D&D（フォルダ内ファイルの移動） =====

  const { resetDropDwell, handleDropDwellOver, isDropDwellSatisfied } =
    useDropDwellGate();

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      resetDropDwell();
      const data = event.active.data.current as
        | { row: FileRow; rootId?: string }
        | undefined;
      if (data?.row && data.rootId) {
        setActiveDrag({ row: data.row, rootId: data.rootId });
      }
    },
    [resetDropDwell],
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const drag = activeDrag;
      const dwellSatisfied = isDropDwellSatisfied(event);
      setActiveDrag(null);
      if (!drag) return;

      // 移動先の上で一瞬しか止まっていないドロップは誤操作として扱う。
      // 移動先が青くなる前に離した = まだ移動しない、は見た目で伝わるので
      // ここでは黙って捨てる。
      if (!dwellSatisfied) return;

      const overData = event.over?.data.current as
        | { node: FileTableFolderNode; rootId: string }
        | undefined;
      if (!overData) return;

      const { node } = overData;
      // 同一ルートフォルダ内のみ・同じフォルダへの移動は無視
      if (overData.rootId !== drag.rootId) return;
      if (drag.row.file.folder?.id === node.id) return;

      try {
        const result = await updateFile({
          project_id: projectId,
          file_id: drag.row.file.id,
          folder_id: node.id,
        }).unwrap();
        onFileUpdated?.(drag.row.messageId, drag.row.file.id, {
          folder: result.data.folder ?? {
            id: node.id,
            name: node.name,
            parent_id: node.parentId,
          },
          updated_at: result.data.updated_at,
        });
        // 移動結果は一覧の表示そのもので分かるため、成功トーストは出さない
        setExpandedFolders((prev) => new Set([...prev, node.id]));
      } catch {
        toast.error("ファイルの移動に失敗しました");
      }
    },
    [activeDrag, projectId, updateFile, onFileUpdated, isDropDwellSatisfied],
  );

  const buildMenuItems = useCallback(
    (row: FileRow): DropdownMenuItem[] => {
      const items: DropdownMenuItem[] = [];
      // オーディオは行クリックが試聴（選択）になるため、波形プレビューと
      // 詳細情報（ACR）はメニューから開く
      if (row.file.file_type === "audio") {
        items.push({
          label: "プレビュー",
          icon: "visibility",
          onClick: () => setPreviewRow(row),
        });
        const detailLabel = getDetailLabelWithAcr(row.file.acr_scan_status);
        items.push({
          label: detailLabel.label,
          color: detailLabel.color,
          icon: "info",
          onClick: () => setDetailRow(row),
        });
      }
      items.push({
        label: "ダウンロード",
        icon: "download",
        onClick: () => handleDownload(row),
      });
      // 文字起こし（動画・オーディオのみ）
      if (row.file.file_type === "audio" || row.file.file_type === "video") {
        items.push({
          label: "文字起こし",
          icon: "speech_to_text",
          onClick: () => setTranscriptionRow(row),
        });
      }
      items.push({
        label: "共有",
        icon: "share",
        onClick: () => handleShare(row),
      });
      items.push({
        label: "リネーム",
        icon: "edit",
        onClick: () => openRename(row),
      });
      // 差し替え・削除は自分の投稿分と自分の陣営（クライアント側/プロデューサー側）の
      // ファイルのみ。相手陣営のファイルは対象外（BE 側でも同条件で検証する）
      if (row.isOwnMessage || row.isFromMyGroup) {
        items.push({
          label: "差し替え",
          icon: "swap_horiz",
          onClick: () => openReplace(row),
          disabled: isReplacing,
        });
        items.push({
          label: "削除",
          icon: "delete",
          onClick: () => setDeleteTarget(row),
        });
      }
      return items;
    },
    [handleDownload, handleShare, openRename, openReplace, isReplacing],
  );

  /**
   * フォルダ配下（サブフォルダ含む）の音声・動画を一括文字起こしの対象にする。
   * 済み / 実行中 / 非対応形式のスキップは BE 側で判定されるので絞り込まない。
   */
  const handleFolderTranscribe = useCallback((node: FileTableFolderNode) => {
    const targets = collectTranscriptionTargets(
      flattenFolderNodeToEntries(node),
      (entry) => ({
        fileId: entry.file.id,
        fileType: entry.file.file_type,
        durationSeconds: parseDurationSeconds(entry.file.file_metadata),
      }),
    );
    if (targets.length === 0) {
      toast.error("このフォルダに音声・動画ファイルがありません");
      return;
    }
    setBulkTranscription({ targets, scopeLabel: `フォルダ「${node.name}」` });
  }, []);

  // フォルダ行の3点リーダー（一括ダウンロードもここに集約）
  const buildFolderMenuItems = useCallback(
    (node: FileTableFolderNode): DropdownMenuItem[] => {
      const items: DropdownMenuItem[] = [
        {
          label: "一括ダウンロード",
          icon: "download",
          onClick: () => handleFolderDownload(node),
          disabled: isZipDownloading || node.totalCount === 0,
        },
        {
          label: "一括文字起こし",
          icon: "speech_to_text",
          onClick: () => handleFolderTranscribe(node),
          disabled: node.totalCount === 0,
        },
        {
          label: "共有",
          icon: "share",
          onClick: () =>
            setShareTarget({
              type: "folder",
              id: node.id,
              name: node.name,
              messageId: node.messageId,
            }),
        },
      ];
      if (node.canEdit) {
        items.push({
          label: "フォルダを作成",
          icon: "create_new_folder",
          onClick: () => {
            setCreateFolderParent(node);
            setCreateFolderName("");
          },
        });
        items.push({
          label: "フォルダを削除",
          icon: "delete",
          onClick: () => setDeleteFolderTarget(node),
          // 誤爆防止のため中身が空のフォルダのみ削除できる
          disabled: node.totalCount > 0,
        });
      }
      return items;
    },
    [handleFolderDownload, handleFolderTranscribe, isZipDownloading],
  );

  // ===== テーブル定義 =====

  const sortHeader = (label: string, field: FileTableSortField) => (
    <>
      <span>{label}</span>
      {sortState.field === field && (
        <span className="material-symbols-rounded" style={{ fontSize: 14 }}>
          {sortState.order === "asc" ? "arrow_upward" : "arrow_downward"}
        </span>
      )}
    </>
  );

  const columns: TableColumn[] = [
    {
      key: "name",
      header: sortHeader("ファイル名", "name"),
      onHeaderClick: () => handleSort("name"),
      minWidth: 160,
    },
    {
      key: "updated",
      header: sortHeader("更新日", "updated"),
      onHeaderClick: () => handleSort("updated"),
      width: 140,
      minWidth: 100,
    },
    {
      key: "size",
      header: sortHeader("サイズ", "size"),
      onHeaderClick: () => handleSort("size"),
      width: 96,
      minWidth: 72,
    },
    { key: "actions", width: 48, minWidth: 48, resizable: false },
  ];

  const hasAnyFiles = allRows.length > 0;

  return (
    <Container $topInset={topInset}>
      {tagItems.length > 0 && (
        <FilterChipBar
          items={tagItems}
          selectedId={effectiveTag}
          onSelect={(id) => setSelectedTag(id as FileTableTag | null)}
        />
      )}

      {!hasAnyFiles && !isLoadingAll ? (
        <EmptyState>
          <p className="bodytext u-text-light-gray">
            アップロードされたファイルはまだありません
          </p>
        </EmptyState>
      ) : (
        <DndContext
          sensors={dndSensors}
          // ポインタが実際に乗っているフォルダ行だけを移動先にする。既定の
          // rectIntersection は掴んだ行の矩形が重なるだけで隣の行を移動先に
          // 選ぶため、意図しないフォルダへ落ちる（FileBrowser と同じ対策）。
          collisionDetection={pointerWithin}
          // 包含判定はドロップ先の座標が古いと当たらないため常に測り直す
          // （既定はドラッグ開始時の 1 回だけ。FileBrowser と同じ理由）。
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          onDragStart={handleDragStart}
          onDragOver={handleDropDwellOver}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveDrag(null)}
        >
          <TableWrapper
            $dropActive={isRootDropOver}
            onDragOver={handleRootDragOver}
            onDragLeave={handleRootDragLeave}
            onDrop={handleRootDrop}
            // フォルダ行側で処理（stopPropagation）されたドロップでも
            // ルートのハイライトは確実に解除する
            onDropCapture={() => setIsRootDropOver(false)}
          >
            <Table columns={columns} minWidth={480}>
              {displayRows.length === 0 && !isLoadingAll ? (
                <tr>
                  <td colSpan={columns.length}>
                    <p className="bodytext-13 u-text-light-gray">
                      該当するファイルがありません
                    </p>
                    {canDropToReceived && (
                      <p className="bodytext-11 u-text-light-gray u-mt4">
                        ここへファイルまたはフォルダをドロップすると、
                        トークルームに投稿せず受領ファイルとして追加できます
                      </p>
                    )}
                  </td>
                </tr>
              ) : (
                displayRows.map((entry) => {
                  if (entry.kind === "folder") {
                    const { node, rootId } = entry;
                    return (
                      <TalkRoomFolderRow
                        key={`folder-${node.id}`}
                        node={node}
                        rootId={rootId}
                        flat={!!entry.flat}
                        isExpanded={expandedFolders.has(node.id)}
                        activeDrag={activeDrag}
                        canNativeDrop={node.canEdit}
                        onToggle={toggleFolder}
                        onExternalDrop={handleExternalDrop}
                        buildMenuItems={buildFolderMenuItems}
                      />
                    );
                  }
                  const { row, depth, rootId } = entry;
                  return (
                    <TalkRoomFileRow
                      key={`file-${row.file.id}`}
                      row={row}
                      depth={depth}
                      rootId={rootId}
                      auditionSelected={audition.selectedKey === row.file.id}
                      auditionPlaying={audition.playingKey === row.file.id}
                      onPreview={setPreviewRow}
                      onShowDetail={setDetailRow}
                      onAuditionToggle={handleAuditionToggle}
                      onAuditionRowRef={handleAuditionRowRef}
                      onAuditionRowClick={handleAuditionRowClick}
                      onAuditionRowKeyDown={handleAuditionRowKeyDown}
                      buildMenuItems={buildMenuItems}
                      transcriptionStatus={transcriptionStatuses[row.file.id]}
                    />
                  );
                })
              )}
              {/* 過去メッセージのファイルを全件読み込むまでのローディング行 */}
              {isLoadingAll && (
                <tr>
                  <td colSpan={columns.length} style={{ whiteSpace: "normal" }}>
                    <InlineLoading visible size={24} />
                    <p
                      className="bodytext-13 u-text-light-gray"
                      style={{ textAlign: "center", margin: 0 }}
                    >
                      残りのファイルを読み込んでいます...
                    </p>
                  </td>
                </tr>
              )}
            </Table>
          </TableWrapper>

          {/* D&D 中にカーソルへ追従するゴースト */}
          <DragOverlay dropAnimation={null}>
            {activeDrag && (
              <DragGhost>
                <span className="material-symbols-rounded">
                  {getFileIconFromCategoryOrExtension(
                    activeDrag.row.file.file_type,
                    activeDrag.row.file.file_name,
                  )}
                </span>
                <span>{activeDrag.row.file.file_name}</span>
              </DragGhost>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {/* 差し替え用の隠しファイル入力 */}
      <input
        ref={replaceInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleReplaceFileSelected}
      />

      {/* プレビューモーダル */}
      {previewRow && (
        <MediaPreviewModal
          src={previewRow.file.optimized_url || previewRow.file.original_url}
          mediaType={toMediaType(previewRow.file.file_type)}
          fileName={previewRow.file.file_name}
          fileId={previewRow.file.id}
          waveformUrl={previewRow.file.waveform_url || undefined}
          acrScanStatus={previewRow.file.acr_scan_status}
          acrScanResult={previewRow.file.acr_scan_result}
          acrScannedAt={previewRow.file.acr_scanned_at}
          fileSize={previewRow.sizeBytes ?? undefined}
          createdAt={previewRow.updatedAt}
          downloadUrl={previewRow.file.original_url}
          onDownload={() =>
            onFileDownload?.(previewRow.messageId, previewRow.file.id)
          }
          onClose={() => setPreviewRow(null)}
        />
      )}

      {/* オーディオの詳細情報（ACR）モーダル */}
      {detailRow && (
        <MediaDetailModal
          onClose={() => setDetailRow(null)}
          fileName={detailRow.file.file_name}
          acrScanResult={detailRow.file.acr_scan_result}
          fileId={detailRow.file.id}
          acrScanStatus={detailRow.file.acr_scan_status}
          acrScannedAt={detailRow.file.acr_scanned_at}
          fileSize={detailRow.sizeBytes ?? undefined}
        />
      )}

      {/* 文字起こしモーダル（オーディオは試聴エンジンへシーク接続） */}
      {transcriptionRow && (
        <MediaTranscriptionModal
          fileId={transcriptionRow.file.id}
          fileName={transcriptionRow.file.file_name}
          onClose={() => setTranscriptionRow(null)}
          onSeek={
            transcriptionRow.file.file_type === "audio"
              ? (seconds) => {
                  auditionRef.current.select(transcriptionRow.file.id);
                  auditionRef.current.seekTo(seconds);
                }
              : undefined
          }
        />
      )}

      {/* フォルダ単位の一括文字起こし */}
      {bulkTranscription && (
        <BulkTranscriptionModal
          targets={bulkTranscription.targets}
          scopeLabel={bulkTranscription.scopeLabel}
          onClose={() => setBulkTranscription(null)}
        />
      )}

      {/* フォルダ一括ダウンロードの進捗モーダル */}
      {downloadState && (
        <BulkDownloadProgressModal
          state={downloadState}
          downloadName={downloadingFolderName}
          onClose={closeDownloadModal}
        />
      )}

      {/* 共有リンクモーダル */}
      {shareTarget && (
        <ShareLinkModal
          projectId={projectId}
          target={shareTarget}
          defaultExpiryDate={projectEndDate}
          onClose={() => setShareTarget(null)}
          onShareStateChanged={handleShareStateChanged}
        />
      )}

      {/* 削除確認ダイアログ */}
      {deleteTarget && (
        <ConfirmModal
          message={`「${deleteTarget.file.file_name}」を削除しますか？`}
          confirmLabel="削除"
          loading={isDeleting}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* フォルダ削除確認ダイアログ */}
      {deleteFolderTarget && (
        <ConfirmModal
          message={`フォルダ「${deleteFolderTarget.name}」を削除しますか？`}
          confirmLabel="削除"
          loading={isDeletingFolder}
          onConfirm={handleDeleteFolderConfirm}
          onCancel={() => setDeleteFolderTarget(null)}
        />
      )}

      {/* フォルダ作成モーダル */}
      {createFolderParent && (
        <Modal
          title={`「${createFolderParent.name}」内にフォルダを作成`}
          titleSize="medium"
          onClose={() => setCreateFolderParent(null)}
          primaryAction={{
            label: "作成",
            onClick: handleCreateFolderConfirm,
            loading: isCreatingFolder,
            disabled: !createFolderName.trim(),
          }}
          secondaryAction={{
            label: "キャンセル",
            onClick: () => setCreateFolderParent(null),
          }}
        >
          <RenameInput
            value={createFolderName}
            autoFocus
            placeholder="フォルダ名"
            onChange={(e) => setCreateFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                e.preventDefault();
                handleCreateFolderConfirm();
              }
            }}
          />
        </Modal>
      )}

      {/* リネームモーダル */}
      {renameTarget && (
        <Modal
          title="ファイル名を変更"
          titleSize="medium"
          onClose={() => setRenameTarget(null)}
          primaryAction={{
            label: "保存",
            onClick: handleRenameConfirm,
            loading: isRenaming,
            disabled: !renameDraft.trim(),
          }}
          secondaryAction={{
            label: "キャンセル",
            onClick: () => setRenameTarget(null),
          }}
        >
          <RenameInput
            value={renameDraft}
            autoFocus
            onChange={(e) => setRenameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                e.preventDefault();
                handleRenameConfirm();
              }
            }}
          />
        </Modal>
      )}

      {/* アップロード部分失敗ダイアログ */}
      {uploadFailureDialog}
    </Container>
  );
};

export default TalkRoomFileTable;
